import React from 'react'
import { Link } from 'react-router-dom'
import HostLink from './HostLink'
import { usePublicSignup } from '../publicConfig'

/**
 * Shared marketing footer. Rendered on the public pages (alongside PublicNav).
 * Keep links in sync with the routes registered in App.jsx. `to` links render
 * as HostLinks, so /signup and /signin cross to the app host (ADR-0062).
 *
 * The Product column's "Start free trial" link is built at render time: during
 * the private beta (public signup closed) it becomes an "Invite only" mailto
 * so the footer never dangles a closed CTA.
 */
function buildColumns(publicSignup) {
  const trialLink = publicSignup
    ? { to: '/signup', label: 'Start free trial' }
    : { href: 'mailto:hello@praenum.com', label: 'Invite only, email us' }
  return [
    {
      heading: 'Product',
      links: [
        { to: '/#how-it-works', label: 'How it works' },
        { to: '/pricing', label: 'Pricing' },
        { to: '/faq', label: 'FAQ' },
        // Plain href, NOT a router Link: /funding/ is static HTML generated at
        // build time and served by nginx before the SPA fallback, so it has to
        // be a real page load. A <Link> would hand it to React Router, which
        // has no such route and would bounce the visitor to "/".
        //
        // It lives here because orphan pages rank poorly: without a link from
        // the app, the funding guides would be reachable only from the sitemap.
        { href: '/funding/', label: 'Funding by state' },
        trialLink,
      ],
    },
    {
      heading: 'Company',
      links: [
        { to: '/about', label: 'About' },
        { to: '/signin', label: 'Sign in' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { to: '/privacy', label: 'Privacy Policy' },
        { to: '/terms', label: 'Terms of Service' },
        { to: '/accessibility', label: 'Accessibility' },
      ],
    },
  ]
}

export default function Footer() {
  const year = new Date().getFullYear()
  const columns = buildColumns(usePublicSignup())
  return (
    <footer className="bg-brand-900 text-gray-300 print:hidden">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-500 text-white font-bold">
                P
              </span>
              <span className="text-lg font-bold text-white">Praenum</span>
            </Link>
            <p className="text-sm text-gray-400 mt-3 max-w-xs">
              Weekly grant matches for small nonprofits, delivered to your inbox.
            </p>
          </div>

          {columns.map(col => (
            <div key={col.heading}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                {col.heading}
              </h4>
              <ul className="space-y-2 text-sm">
                {col.links.map(l => (
                  <li key={(l.to || l.href) + l.label}>
                    {l.href ? (
                      <a href={l.href} className="text-gray-300 hover:text-white transition-colors">
                        {l.label}
                      </a>
                    ) : (
                      <HostLink to={l.to} className="text-gray-300 hover:text-white transition-colors">
                        {l.label}
                      </HostLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-400">
          <p>© {year} Praenum. All rights reserved.</p>
          <a href="mailto:hello@praenum.com" className="hover:text-white transition-colors">
            hello@praenum.com
          </a>
        </div>
      </div>
    </footer>
  )
}
